import React from "react";
import { useTranslation } from "react-i18next";
import { Bus, CircleDollarSign, Landmark } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { hotelService } from "@/services/hotel-service-service";
import {
  StatsSummaryBlock,
  type StatsSummaryCard,
} from "@/components/common/StatsSummaryBlock";
import { formatCurrencyAmount } from "@/components/pages/services/utils/priceFormatting";

interface PartnersServicesStatsProps {
  hotelId: string;
  currency?: string;
}

const badgeClassName =
  "inline-flex max-w-full w-fit items-center justify-center rounded-md bg-royal-glow-5 px-2 py-0.5 text-2xs font-semibold leading-none text-royal-glow-2";

const PartnersServicesStats: React.FC<PartnersServicesStatsProps> = ({
  hotelId,
  currency,
}) => {
  const { t } = useTranslation();

  const { data: topRevenueService } = useQuery({
    queryKey: ["hotel", hotelId, "partners", "top-revenue"],
    queryFn: () => hotelService.getTopRevenuePartnerOrders(hotelId),
    enabled: !!hotelId,
  });

  const { data: topBookedService } = useQuery({
    queryKey: ["hotel", hotelId, "partners", "top-booked"],
    queryFn: () => hotelService.getTopBookedPartnerOrders(hotelId),
    enabled: !!hotelId,
  });

  const { data: partnersOrdersSummary } = useQuery({
    queryKey: ["hotel", hotelId, "partners", "orders-summary"],
    queryFn: () => hotelService.getPartnersOrdersSummary(hotelId),
    enabled: !!hotelId,
  });

  const cards: StatsSummaryCard[] = [
    {
      key: "biggestRevenue",
      label: topRevenueService?.serviceName ?? "—",
      value: topRevenueService
        ? formatCurrencyAmount(topRevenueService.totalRevenue, currency)
        : "—",
      icon: Landmark,
      valueClassName: "text-green-600",
      badge: (
        <span className={badgeClassName}>
          {t("services.page.summary.biggestRevenue", "Biggest Revenue")}
        </span>
      ),
    },
    {
      key: "totalRevenue",
      label: t(
        "services.page.summary.fromPartnersServices",
        "From Partners Services",
      ),
      value: partnersOrdersSummary
        ? formatCurrencyAmount(
            partnersOrdersSummary.partnerRevenue,
            partnersOrdersSummary.currency,
          )
        : "—",
      icon: CircleDollarSign,
      valueClassName: "text-green-600",
      badge: (
        <span className={badgeClassName}>
          {t("services.page.summary.totalRevenue", "Total Revenue")}
        </span>
      ),
    },
    {
      key: "forMonth",
      label: topBookedService?.serviceName ?? "—",
      value:
        topBookedService != null
          ? topBookedService.totalBookings.toLocaleString("en-GB")
          : "—",
      icon: Bus,
      valueClassName: "text-monochrome-core-1",
      badge: (
        <span className={badgeClassName}>
          {t("services.page.summary.forMonth", "For Month")}
        </span>
      ),
    },
  ];

  return <StatsSummaryBlock cards={cards} />;
};

export default PartnersServicesStats;
